import React from 'react'
import { makeStyles } from '@mui/styles'
import { darken } from '@mui/material/styles'
import { List, Paper, Divider, ListItem, ListItemText } from '@mui/material'
import { useSelector } from 'react-redux'
import { searchResultsSlice } from '../../../reducers'
import { PaidConsumerSearchHistory } from './paid-consumer-search-history'
import { PaidConsumerRecommendedKeywords } from './paid-consumer-recommended-keywords'

const useStyles = makeStyles(({ palette, spacing, typography, shadows }) => {
  const textColor = '#212121'
  return {
    searchSuggestions: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: `${palette.common.white} !important`,
      color: '#4F4F4F',
      zIndex: 1400,
      borderRadius: spacing(1.25),
      boxShadow: shadows[8],
      border: `1px solid ${palette.grey[300]} !important`
    },
    searchSuggestionsList: {
      paddingBottom: 0,
      '& .MuiListItem-divider': {
        borderBottom: `1px ${palette.grey[300]} solid`
      }
    },
    seeAllResultsButton: {
      background: palette.grey[200],
      padding: spacing(1.5, 2),
      border: 'none',
      borderRadius: `0 0 ${spacing(1.25)} ${spacing(1.25)}`,
      cursor: 'pointer',
      '& span': {
        fontWeight: typography.fontWeightMedium,
        ...typography.body1,
        color: textColor
      },
      '&:hover, &:focus': {
        background: darken(palette.grey[200], 0.1),
        outline: 'none'
      }
    },
    divider: {
      backgroundColor: palette.grey[300]
    },
    searchAllText: {
      '& span': {
        ...typography.body1,
        fontWeight: typography.fontWeightMedium
      }
    }
  }
})

export const PaidConsumerSearchSuggestions = ({
  open,
  searchInput,
  onSuggestionSelect,
  onHistoryEdit,
  isDeleting
}) => {
  const classes = useStyles()
  const history = useSelector(searchResultsSlice.selectors.selectHistory)
  const suggestions = useSelector(searchResultsSlice.selectors.selectSuggestions)

  const showAll = searchInput?.length > 2
  const showSuggestions = showAll && !!suggestions?.length
  const showHistory = !!history?.length

  if (!open || (!showAll && !showSuggestions && !showHistory)) return null

  return (
    <Paper className={classes.searchSuggestions} elevation={3}>
      <List
        className={classes.searchSuggestionsList}
        data-test='paid-consumer-search-suggestions-list'
      >
        {showSuggestions && (
          <PaidConsumerRecommendedKeywords
            suggestions={suggestions}
            onSuggestionSelect={onSuggestionSelect}
          />
        )}
        {showSuggestions && showHistory && (
          <li>
            <Divider className={classes.divider} />
          </li>
        )}
        {showHistory && (
          <PaidConsumerSearchHistory
            history={history}
            onSuggestionSelect={onSuggestionSelect}
            onHistoryEdit={onHistoryEdit}
            isDeleting={isDeleting}
          />
        )}
        {showHistory && showAll && (
          <li>
            <Divider className={classes.divider} />
          </li>
        )}
        {showAll && (
          <ListItem
            onClick={() => {
              onSuggestionSelect(searchInput, 'see-all-results', 'search-suggestions')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSuggestionSelect(searchInput, 'see-all-results', 'search-suggestions')
              }
            }}
            className={classes.seeAllResultsButton}
            data-test='paid-consumer-search-all-results'
            tabIndex={0}
            role='button'
            aria-label='See all results page'
          >
            <ListItemText
              className={classes.searchAllText}
            >
              See all results page
            </ListItemText>
          </ListItem>
        )}
      </List>
    </Paper>
  )
}
